import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress, InputAdornment } from '@mui/material';
import { Person } from '@mui/icons-material';
import { searchGraphUsers, GraphUser } from '../../../../../utils/MsGraphApiCall';

interface ApproverNameFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export const ApproverNameField: React.FC<ApproverNameFieldProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Approver Name',
  required = false,
  error = false,
  helperText,
}) => {
  const [users, setUsers] = useState<GraphUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<GraphUser | null>(null);

  // Initialize selected user if there's an existing value
  useEffect(() => {
    if (value && !selectedUser) {
      // If we have a value but no selected user, create a mock user object
      if (value.includes('@')) {
        // Looks like an email
        setSelectedUser({
          id: value,
          displayName: value,
          mail: value,
          userPrincipalName: value,
          accountEnabled: true
        });
      } else {
        // Treat as display name
        setSelectedUser({
          id: value,
          displayName: value,
          mail: '',
          userPrincipalName: value,
          accountEnabled: true
        });
      }
    }
  }, [value, selectedUser]);

  // Debounced search function
  useEffect(() => {
    if (searchQuery.length < 2) {
      setUsers([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const searchResults = await searchGraphUsers(searchQuery);
        setUsers(searchResults);
      } catch (error) {
        console.error('Error searching users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleUserSelect = (event: any, user: GraphUser | null) => {
    setSelectedUser(user);
    if (user) {
      // Store the user's display name as the approver value
      onChange(user.displayName || user.mail);
    } else {
      onChange('');
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setSearchQuery(newInputValue);
  };

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={users}
      loading={loading}
      value={selectedUser}
      onChange={handleUserSelect}
      inputValue={searchQuery}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disabled={disabled}
      noOptionsText={searchQuery.length < 2 ? "Type at least 2 characters to search" : "No users found"}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Person sx={{ color: '#64748b', fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {option.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.mail}
            </Typography>
            {option.jobTitle && (
              <Typography variant="caption" color="text.secondary" display="block">
                {option.jobTitle} {option.department && `• ${option.department}`}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    />
  );
};
